import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { Button, Table } from "reactstrap";
import { PUBLISH_HISTORY_LOGS } from "../../../graphql/publish-gql";
import { Pageable } from "../../../types/common";
import { IPublishHistory, IPublishHistoryResponse } from "../../../types/log";
import DateFormat, { DATE_TIME_FORMAT } from "../../common/date-format";
import { CheckIcon, CloseIcon, RefreshIcon, TreeCollapseIcon, TreeExpandIcon } from "../../common/icons";
import TableHeaders, { TableHeaderInfo } from "../../common/table-header";
import "./publish.scss";
import SpinnerOverlay from "../../common/spinner/spinner";

const TABLE_HEADERS: TableHeaderInfo[] = [
    { cssClass: "icon-column"},
    { header: 'ID', cssClass: "column-7rem" },
    { header: 'Step' },
    { header: 'Start Time', cssClass: "date-column date-column-standard" },
    { header: 'End Time', cssClass: "date-column date-column-standard" },
    { header: 'Result', cssClass: "column-7rem" },
    { header: 'Log', cssClass: "column-7rem" }
];

const PublishHistory = (props: Pageable) => {
    const [results, setResults] = useState<IPublishHistory[]>()

    const [getPublishHistory, { loading, data }] = useLazyQuery<IPublishHistoryResponse>(PUBLISH_HISTORY_LOGS, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            setResults(data?.publishHistoryLogs)
        }
    })

    const [expandStates, setExpandStates] = useState<Map<number, boolean>>()

    useEffect(() => {
        getPublishHistory()
    }, [])

    const toggle = useCallback((publishHistoryId: number) => {
        const newMap = new Map(expandStates);
        newMap.set(publishHistoryId, !expandStates?.get(publishHistoryId));
        setExpandStates(newMap);
    }, [expandStates])

    const onRefresh = (event:any) => {
        event.preventDefault()
        event.stopPropagation()
        getPublishHistory()
    }

    return (
        <section>
            {loading && <SpinnerOverlay/>}
            <div className="publish-history-refresh">
                <Button color="link" className="ms-auto" aria-label="Refresh" onClick={onRefresh}>
                    <RefreshIcon />
                </Button>
            </div>

            <Table striped hover className="fixed" id="publish-history">
                <TableHeaders headers={TABLE_HEADERS} />

                {results && results.length > 0
                    ?
                    <tbody>
                        {results.map((item: IPublishHistory) => {
                            const logSize = item.publishSteps.length;

                            return item.publishSteps.map((log, index) => {
                                const isNewRecord = index === 0;
                                const isExpanded = !!expandStates?.get(item.publishHistoryId);

                                if (!isNewRecord && !isExpanded) return <></>;

                                const isToggle = isNewRecord && logSize > 1;
                                const isCollapsedToggle = isToggle && !isExpanded;

                                return (
                                    <tr key={log.serialId} className={isToggle ? 'pe-force' : ' '}
                                        onClick={() => isToggle && toggle(item.publishHistoryId)}
                                        onKeyUp={(e) => (e.key === 'Enter' || e.key === ' ') && isToggle && toggle(item.publishHistoryId)}>

                                        <td className="small">
                                            {isNewRecord && isToggle && (isExpanded ? <TreeCollapseIcon /> : <TreeExpandIcon />)}
                                        </td>
                                        <td>{isNewRecord && item.publishHistoryId}</td>
                                        <td className="text-nowrap"> {log.step} - {log.stepDef}
                                        </td>
                                        <td className="text-nowrap">{isCollapsedToggle
                                            ? <DateFormat date={item.publishSteps[0].startDate} format={DATE_TIME_FORMAT} />
                                            : <DateFormat date={log.startDate} format={DATE_TIME_FORMAT} />}
                                        </td>
                                        <td className="text-nowrap">{isCollapsedToggle
                                            ? <DateFormat date={item.publishSteps[0].endDate} format={DATE_TIME_FORMAT} />
                                            : <DateFormat date={log.endDate} format={DATE_TIME_FORMAT} />}
                                        </td>
                                        <td className="text-nowrap">{isCollapsedToggle
                                            ? <>{item.publishSteps[0].result ? <CheckIcon /> : item.publishSteps[0].result===false ? <CloseIcon /> : ''} {item.publishSteps[0].result ? 'Success' : item.publishSteps[0].result===false? 'Fail': ''}</>
                                            : <>{log.result ? <CheckIcon /> : log.result===false ? <CloseIcon /> : ''} {log.result ? 'Success' : log.result===false? 'Fail': ''}</>}
                                        </td>
                                        <td>{isCollapsedToggle
                                            ? <>{item.publishSteps[0].log === 'Success' ? <CheckIcon /> : !!item.publishSteps[0].log ? <CloseIcon /> : ''} {item.publishSteps[0].log}</>
                                            : <>{log.log === 'Success' ? <CheckIcon /> : !!log.log  ? <CloseIcon /> : ''} {log.log}</>}
                                        </td>
                                    </tr>
                                );
                            });
                        })}
                    </tbody>
                    :
                    <tfoot>
                        <tr>
                            <td colSpan={TABLE_HEADERS.length} className="text-center">
                                {results?.length === 0
                                    ? 'No publish history found.'
                                    : <>Publish history will appear in this table.</>
                                }
                            </td>
                        </tr>
                    </tfoot>
                }
            </Table>

        </section>
    )
}

export default PublishHistory
